import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import {
  fetchMyFavoriteLearningResources,
  unfavoriteLearningResource,
  fetchMyAccessHistory,
  fetchMyUnlockedResources
} from '@/service/api';
import { useLearningCategoryStore } from '@/stores';
import heroIllustration from '@/assets/home/learning-course.png';
import '@/styles/fluid-pages.css';

const ITEMS_PER_PAGE = 12;

type ViewMode = 'card' | 'list';
type TabType = 'favorites' | 'history' | 'unlocked';

const tabs: { key: TabType; label: string; icon: string }[] = [
  { key: 'favorites', label: '收藏', icon: 'ri-bookmark-3-line' },
  { key: 'history', label: '学习历史', icon: 'ri-history-line' },
  { key: 'unlocked', label: '已解锁内容', icon: 'ri-key-2-line' }
];

// 解析各 Tab 下的数据项，提取关联资源信息与时间属性
function getCourseMeta(item: any, activeTab: TabType): {
  course?: Api.Learning.ResourceResponse;
  metaText?: string;
  metaIcon?: string;
} {
  if (!item) return {};
  if (activeTab === 'favorites') {
    return { course: item };
  } else if (activeTab === 'history') {
    const historyItem = item as Api.Learning.AccessHistoryResponse;
    const dateStr = historyItem.last_accessed_at
      ? new Date(historyItem.last_accessed_at).toLocaleDateString('zh-CN', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
      : '';
    return {
      course: historyItem.resource,
      metaText: dateStr ? `最近学习 ${dateStr}` : undefined,
      metaIcon: 'ri-time-line'
    };
  } else if (activeTab === 'unlocked') {
    const unlockedItem = item as Api.Learning.UnlockedResourceResponse;
    const dateStr = unlockedItem.unlocked_at
      ? new Date(unlockedItem.unlocked_at).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      })
      : '';
    return {
      course: unlockedItem.resource,
      metaText: dateStr ? `解锁于 ${dateStr}` : '已解锁',
      metaIcon: 'ri-checkbox-circle-line'
    };
  }
  return { course: item };
}

export default function MyLearningPage() {
  const [activeTab, setActiveTab] = useState<TabType>('favorites');
  const fetchCategories = useLearningCategoryStore((state) => state.fetchCategories);
  const [viewMode, setViewMode] = useState<ViewMode>('card');
  const [currentPage, setCurrentPage] = useState(1);

  const [items, setItems] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const lastFetchParamsRef = useRef<string | null>(null);

  // 获取分类列表
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 加载数据
  const loadData = useCallback(async (force = false) => {
    const queryParams: Api.Learning.PaginationParams = {
      page: currentPage,
      page_size: ITEMS_PER_PAGE
    };

    const paramsKey = JSON.stringify({ activeTab, queryParams });
    if (!force && lastFetchParamsRef.current === paramsKey) {
      return;
    }
    lastFetchParamsRef.current = paramsKey;

    setLoading(true);
    try {
      if (activeTab === 'favorites') {
        const res = await fetchMyFavoriteLearningResources(queryParams);
        setItems(res?.items || []);
        setTotalCount(res?.total || 0);
      } else if (activeTab === 'history') {
        const res = await fetchMyAccessHistory(queryParams);
        setItems(res?.items || []);
        setTotalCount(res?.total || 0);
      } else if (activeTab === 'unlocked') {
        const res = await fetchMyUnlockedResources(queryParams);
        setItems(res?.items || []);
        setTotalCount(res?.total || 0);
      }
    } catch (err) {
      console.error('获取学习资源数据失败', err);
      setItems([]);
      setTotalCount(0);
      lastFetchParamsRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [activeTab, currentPage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 切换 Tab 时重置页码
  const handleTabChange = (tab: TabType) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setCurrentPage(1);
    lastFetchParamsRef.current = null;
  };

  // 取消收藏逻辑
  const handleUnfavorite = async (e: React.MouseEvent, resourceId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!resourceId) return;

    setActionLoadingId(resourceId);
    try {
      await unfavoriteLearningResource(resourceId);
      // 从本地列表中移除该资源并扣减总数
      setItems((prev) =>
        prev.filter((item) => {
          const { course } = getCourseMeta(item, activeTab);
          return course?.id !== resourceId;
        })
      );
      setTotalCount((prev) => Math.max(0, prev - 1));

      // 若当前页清空且非第一页，跳至前一页
      if (items.length === 1 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      }
    } catch (err) {
      console.error('取消收藏失败', err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const emptyTextMap: Record<TabType, { title: string; desc: string }> = {
    favorites: {
      title: '暂无收藏资源',
      desc: '在浏览课程时点击收藏按钮，方便在此随时查看与学习'
    },
    history: {
      title: '暂无学习历史',
      desc: '您还没有学习过任何课程，快去探索感兴趣的课程吧'
    },
    unlocked: {
      title: '暂无已解锁内容',
      desc: '您还没有解锁过付费或专享课程'
    }
  };

  return (
    <div className="courses-page min-h-screen flex flex-col bg-white">
      <Navbar />

      <div className="site-shell max-w-7xl mx-auto px-4 md:px-8 pt-16 sm:pt-20 pb-16 flex-1 flex flex-col w-full">
        {/* 面包屑与页头 Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              <Link to="/" className="hover:text-emerald-600 transition-colors">首页</Link>
              <span>/</span>
              <Link to="/courses" className="hover:text-emerald-600 transition-colors">课程中心</Link>
              <span>/</span>
              <span className="text-gray-900 font-medium">我的学习</span>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">我的学习</h1>
              <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600 font-medium border border-emerald-100">
                共 {totalCount} 门
              </span>
            </div>
          </div>

          <Link
            to="/courses"
            className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-emerald-600 font-medium transition-colors cursor-pointer self-start md:self-auto"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            <span>返回课程中心</span>
          </Link>
        </div>

        {/* 主选项卡切换 (Tabs) + 右侧视图切换 */}
        <div className="flex items-center justify-between border-b border-gray-200 mb-6">
          <div className="flex items-center gap-6">
            {tabs.map((t) => {
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => handleTabChange(t.key)}
                  className={`inline-flex items-center gap-2 py-3 px-1 text-sm font-semibold border-b-2 transition-all cursor-pointer ${isActive
                    ? 'border-emerald-500 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                    }`}
                >
                  <i className={`${t.icon} text-base`}></i>
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>

          {/* 右侧：视图切换按钮 */}
          <div className="flex items-center p-0.5 border border-gray-200 rounded-lg bg-gray-50/60 mb-2 shrink-0">
            <button
              type="button"
              title="卡片视图"
              onClick={() => setViewMode('card')}
              className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition-all cursor-pointer ${viewMode === 'card'
                ? 'bg-emerald-50 text-emerald-600 shadow-2xs font-bold'
                : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              <i className="ri-grid-fill text-sm"></i>
            </button>
            <button
              type="button"
              title="列表视图"
              onClick={() => setViewMode('list')}
              className={`inline-flex items-center justify-center w-6 h-6 rounded-md transition-all cursor-pointer ${viewMode === 'list'
                ? 'bg-emerald-50 text-emerald-600 shadow-2xs font-bold'
                : 'text-gray-400 hover:text-gray-600'
                }`}
            >
              <i className="ri-list-check text-sm"></i>
            </button>
          </div>
        </div>

        {/* 资源列表区 */}
        <div className="flex-1 flex flex-col">
          {loading && items.length === 0 ? (
            <div className="text-center py-24 flex-1 flex flex-col items-center justify-center">
              <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-3"></div>
              <p className="text-gray-400 text-sm">正在获取数据...</p>
            </div>
          ) : !loading && items.length === 0 ? (
            <div className="text-center py-24 flex-1 flex flex-col items-center justify-center bg-gray-50/40 rounded-2xl border border-dashed border-gray-200 my-4">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4 text-emerald-500">
                <i className={`${tabs.find((t) => t.key === activeTab)?.icon || 'ri-book-open-line'} text-3xl`}></i>
              </div>
              <h3 className="text-base font-medium text-gray-800 mb-1">{emptyTextMap[activeTab].title}</h3>
              <p className="text-xs text-gray-400 max-w-sm mb-6">
                {emptyTextMap[activeTab].desc}
              </p>
              <Link
                to="/courses"
                className="px-5 py-2 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-all cursor-pointer shadow-xs"
              >
                前往探索课程
              </Link>
            </div>
          ) : (
            <div className={`transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
              {viewMode === 'card' ? (
                <div className="courses-card-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {items.map((item, idx) => {
                    const { course, metaText, metaIcon } = getCourseMeta(item, activeTab);
                    if (!course) return null;
                    return (
                      <CourseCardItem
                        key={course.id || idx}
                        course={course}
                        metaText={metaText}
                        metaIcon={metaIcon}
                        activeTab={activeTab}
                        actionLoading={actionLoadingId === course.id}
                        onUnfavorite={(e) => handleUnfavorite(e, course.id)}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {items.map((item, idx) => {
                    const { course, metaText, metaIcon } = getCourseMeta(item, activeTab);
                    if (!course) return null;
                    return (
                      <CourseListItem
                        key={course.id || idx}
                        course={course}
                        metaText={metaText}
                        metaIcon={metaIcon}
                        activeTab={activeTab}
                        actionLoading={actionLoadingId === course.id}
                        onUnfavorite={(e) => handleUnfavorite(e, course.id)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 分页 */}
        {items.length > 0 && totalPages > 1 && (
          <div className="mt-8 pt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 rounded-full text-xs font-medium transition-all cursor-pointer ${page === currentPage
                  ? 'bg-emerald-500 text-white shadow-xs'
                  : 'text-gray-600 hover:bg-gray-100'
                  }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// 卡片视图组件
function CourseCardItem({
  course,
  metaText,
  metaIcon,
  activeTab,
  actionLoading,
  onUnfavorite
}: {
  course: Api.Learning.ResourceResponse;
  metaText?: string;
  metaIcon?: string;
  activeTab: TabType;
  actionLoading: boolean;
  onUnfavorite: (e: React.MouseEvent) => void;
}) {
  const coverUrl = course.cover_asset?.access_url || heroIllustration;
  const isVideo = course.resource_type === 'VIDEO' || !course.resource_type;
  const isFree = course.access_mode === 'FREE' || !course.points_price;
  const lessonCount = course.lesson_count || (isVideo ? 12 : 0);

  return (
    <div className="group relative flex flex-col bg-white rounded-2xl shadow-2xs hover:shadow-lg transition-all duration-300 overflow-hidden hover:-translate-y-1 border border-gray-100/80">
      <Link to={`/courses/${course.id}`} className="block flex-1 flex flex-col">
        {/* 封面图容器 */}
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-100 flex-shrink-0">
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={course.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-white p-4">
              <div className="text-center">
                <i className={`${isVideo ? 'ri-clapperboard-line' : 'ri-book-open-line'} text-2xl text-slate-400 mb-1`}></i>
                <span className="text-xs text-slate-300 font-medium line-clamp-1">{course.title}</span>
              </div>
            </div>
          )}

          {/* 左上角 Badge：视频 / 文章 */}
          <div className="absolute top-2.5 left-2.5 pointer-events-none flex items-center gap-1.5">
            {isVideo ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#7c4dff] text-white shadow-xs">
                视频
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-[#ff9800] text-white shadow-xs">
                文章
              </span>
            )}
          </div>

          {/* 历史收藏模式下的取消收藏按钮 */}
          {activeTab === 'favorites' && (
            <button
              type="button"
              onClick={onUnfavorite}
              disabled={actionLoading}
              title="取消收藏"
              className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-black/40 hover:bg-rose-500 text-white backdrop-blur-xs flex items-center justify-center transition-all cursor-pointer opacity-90 group-hover:opacity-100 z-10"
            >
              {actionLoading ? (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <i className="ri-bookmark-fill text-sm"></i>
              )}
            </button>
          )}

          {/* 右下角 Badge：课节数 */}
          {lessonCount > 0 && (
            <div className="absolute bottom-2.5 right-2.5 pointer-events-none">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-normal bg-black/40 text-white backdrop-blur-xs">
                共 {lessonCount} 节
              </span>
            </div>
          )}
        </div>

        {/* 卡片主体 */}
        <div className="flex-1 flex flex-col p-3.5">
          <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug truncate mb-2.5 group-hover:text-emerald-600 transition-colors">
            {course.title}
          </h3>

          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-1 shrink-0 text-gray-400">
              <i className="ri-user-3-line text-xs"></i>
              <span>{course.view_count || 0}人学习</span>
            </div>

            {metaText && (
              <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                {metaIcon && <i className={`${metaIcon} text-xs`}></i>}
                <span>{metaText}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-auto pt-1">
            <div>
              {isFree ? (
                <span className="text-sm sm:text-base font-bold text-[#00c9a7]">免费</span>
              ) : (
                <span className="text-sm sm:text-base font-bold text-[#ff6b00]">
                  {course.points_price} <span className="text-xs font-semibold">积分</span>
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

// 列表视图组件
function CourseListItem({
  course,
  metaText,
  metaIcon,
  activeTab,
  actionLoading,
  onUnfavorite
}: {
  course: Api.Learning.ResourceResponse;
  metaText?: string;
  metaIcon?: string;
  activeTab: TabType;
  actionLoading: boolean;
  onUnfavorite: (e: React.MouseEvent) => void;
}) {
  const getCategoryNameByCode = useLearningCategoryStore((state) => state.getCategoryNameByCode);
  const categoryName = getCategoryNameByCode(course.category_code, '通用课程');

  const coverUrl = course.cover_asset?.access_url || heroIllustration;
  const isFree = course.access_mode === 'FREE' || !course.points_price;
  const studyCount = (course as any).study_count ?? (course as any).view_count ?? 0;
  const lessonText = course.lesson_count ? `${course.lesson_count}节课` : '0节';

  return (
    <div className="group bg-white rounded-2xl border border-gray-100 shadow-2xs hover:shadow-md transition-all duration-300 p-3.5 flex items-center gap-4">
      <Link to={`/courses/${course.id}`} className="relative w-44 aspect-[16/10] rounded-xl overflow-hidden shrink-0 hidden sm:block bg-slate-100">
        <img
          src={coverUrl}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </Link>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700">
            {categoryName}
          </span>
          {metaText && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full font-medium">
              {metaIcon && <i className={`${metaIcon} text-xs`}></i>}
              {metaText}
            </span>
          )}
        </div>
        <Link to={`/courses/${course.id}`}>
          <h3 className="font-bold text-slate-800 text-base transition-colors group-hover:text-emerald-600 line-clamp-1">
            {course.title}
          </h3>
        </Link>
        <p className="text-xs text-slate-400 mt-1">{studyCount}人正在学习 · {lessonText}</p>
      </div>

      <div className="shrink-0 flex items-center gap-4 pl-4 border-l border-slate-100">
        <div className="font-bold">
          {isFree ? (
            <span className="text-base font-bold text-[#00c9a7]">免费</span>
          ) : (
            <span className="text-lg font-bold text-[#ff6b00]">
              {course.points_price} <span className="text-xs font-semibold">积分</span>
            </span>
          )}
        </div>

        {activeTab === 'favorites' && (
          <button
            type="button"
            onClick={onUnfavorite}
            disabled={actionLoading}
            title="取消收藏"
            className="w-8 h-8 rounded-full border border-gray-200 text-gray-400 hover:text-rose-500 hover:border-rose-200 hover:bg-rose-50 flex items-center justify-center transition-all cursor-pointer"
          >
            {actionLoading ? (
              <div className="w-3.5 h-3.5 border-2 border-rose-500 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <i className="ri-bookmark-fill text-sm text-emerald-500 hover:text-rose-500"></i>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
